import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import { useLocation } from 'react-router-dom';
import i18next from 'i18next';
import Button from '@mui/material/Button';
import { Box, DialogActions, DialogTitle, Grid, LinearProgress, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '@mui/material/IconButton';
import { makeStyles } from '@mui/styles';
import { withRouter } from '@/hooks';
import { isEqual } from 'underscore';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import Logo from '../Common/Logo/Logo';
import { NewTextField } from '../Common/Fields';
import auth from '@/apis/auth';
import { login } from '@/actions/authActions';
import CustomToast from '../VehicleEditor/CustomToast';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'flex-start',
    [theme.breakpoints.up('md')]: { justifyContent: 'center', padding:'48px 0' },
  },
  appContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: '0 auto',
    boxShadow: 'none',
    [theme.breakpoints.up('lg')]: { width: 'auto' },
    [theme.breakpoints.up('md')]: { margin: '0 20px' },
    [theme.breakpoints.between('sm', 'md')]: { justifyContent: 'unset', width: '100%', padding: '10px', margin: '0 5px' },
    [theme.breakpoints.down('sm')]: { width: '100%', margin: '0 5px', padding: '10px', justifyContent: 'unset' },
  },
  cardLayout: {
    width: '352px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    padding: '40px 24px',
    borderRadius: '8px',
    margin: '0 auto',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.up('lg')]: { width: '100%', maxWidth: '416px', padding: '40px 30px' },
    [theme.breakpoints.between('sm', 'md')]: { width:'100%', boxShadow:'none', borderRadius: '0', maxWidth:'416px', padding:'50px 32px' },
    [theme.breakpoints.down('sm')]: { boxShadow:'none' },
  },
  customIcon: { color:'theme.palette.primary.main' },
  formSignin: { margin: '0 auto' },
  ButtonStyle: {
    height: '50px',
    width: '100%',
    margin: theme.spacing(3, 0, 0, 0),
    backgroundColor: theme.palette.primary.main,
    borderRadius:'4px',
    [theme.breakpoints.up('lg')]: { height: '50px', fontSize: '16px' },
    [theme.breakpoints.between('sm', 'md')]: { height: '50px', fontSize: '14px', margin: theme.spacing(2, 0, 0, 0) },
    [theme.breakpoints.down('sm')]: { height: '45px', fontSize: '12px', margin: theme.spacing(1, 0, 0, 0) },
  }
}));

interface LoginProps {
  router: any;
}

const Login: React.FC<LoginProps> = () => {
  const [dialog, setDialog] = useState(false);
  const [creds, setCreds] = useState({ email: '', site_name: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const { loading } = useSelector((state: any) => state.core, isEqual);
  const location = useLocation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [toast, setToast] = useState<{
    open: boolean;
    severity: 'success' | 'error' | 'info' | 'warning';
    msg: string;
  }>({ open: false, severity: 'success', msg: '' });

  const handleChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    setCreds(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async () => {
    const nextPathname = (location as any).state?.from?.pathname;
    try {
      await (dispatch as any)(login(creds.email, creds.password, creds.site_name, nextPathname));
    } catch (err: any) {
      setToast({
        open: true,
        severity: 'error',
        msg: err?.__uiMessage || i18next.t('common:login.errorMsg'),
      });
    }
  };

  const toggleDialog = () => setDialog(v => !v);

  const requestPasswordReset = () => {
    const data = JSON.stringify({ email: creds.email });
    return auth.reset(data)
      .then(() => {
        setToast({
          open: true,
          severity: 'success',
          msg: i18next.t('common:states.resetPass'),
        });
        toggleDialog();
      })
      .catch(error => {
        if (error && error.response && error.response.status === 422) {
          setToast({
            open: true,
            severity: 'error',
            msg: i18next.t('common:states.invalidEmailAddress'),
          });
        } else {
          setToast({
            open: true,
            severity: 'error',
            msg: i18next.t('common:states.someErr'),
          });
        }
        toggleDialog();
      });
  };
  
  return (
    <Box className={classes.root}>
      {loading &&
        <LinearProgress style={{ position: 'fixed', top: 0, left: 0, right: 0 }} color={'primary'} />
      }

      {/* CustomToast renderer */}
      <CustomToast
        open={toast.open}
        severity={toast.severity}
        message={toast.msg}
        autoHideMs={3000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      />

      <Paper className={classes.appContent}>
        <Grid className={classes.cardLayout}>
          <Grid className="Logo">
            <Logo w='300' h='50'/>
          </Grid>
          <form
            className='form-signin'
            onSubmit={e => { e.preventDefault(); handleSubmit(); }}
          >
            <Grid>
              <NewTextField
                fullWidth
                onChange={handleChange}
                value={creds.email}
                name={'email'}
                autoComplete="email"
                disabled={loading}
                type={'email'}
                inputProps={{ 'data-cy': 'email' }}
                labelProps={{}}
                label={i18next.t('common:login.username')}
              />
            </Grid>
            <Grid>
              <NewTextField
                fullWidth
                onChange={handleChange}
                value={creds.password}
                name={'password'}
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                inputProps={{ 'data-cy': 'password' }}
                label={i18next.t('common:login.password')}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                      {showPassword
                        ? <VisibilityOff fontSize="small" className="customIcon" />
                        : <Visibility fontSize="small" className="customIcon" />
                      }
                    </IconButton>
                  )
                }}
              />
            </Grid>
            <Grid>
              <NewTextField
                fullWidth
                onChange={handleChange}
                value={creds.site_name}
                name={'site_name'}
                disabled={loading}
                type={'site_name'}
                inputProps={{ 'data-cy': 'site_name' }}
                labelProps={{}}
                label={i18next.t('common:login.sitename')}
              />
            </Grid>
            <Grid container justifyContent={'end'}>
              <Button variant={'text'} onClick={toggleDialog}>
                <Typography variant={'button'} fontSize={12}>
                  {i18next.t('common:login.forgot')}
                </Typography>
              </Button>
            </Grid>
            <Grid>
              <Button
                disabled={loading}
                className={classes.ButtonStyle}
                data-cy={'submit'}
                type={'submit'}
                variant={'contained'}
                color={'primary'}
              >
                {i18next.t('common:login.login')}
              </Button>
            </Grid>
          </form>
        </Grid>
      </Paper>

      <Dialog open={dialog} onClose={toggleDialog} maxWidth={'sm'} fullWidth>
        <DialogTitle>{i18next.t('common:login.reset')}</DialogTitle>
        <Grid>
          <Grid container alignItems="center">
            <NewTextField
              onChange={handleChange}
              value={creds.email}
              name={'email'}
              type={'email'}
              label={i18next.t('common:login.username')}
              data-cy={'email'}
            />
          </Grid>
        </Grid>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={requestPasswordReset}>
            {i18next.t('common:login.request')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

Login.propTypes = {
  router: PropTypes.object.isRequired
};

export default withRouter(Login);
